import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Megaphone, Plus, Trash2, Bot, Pin, PinOff } from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin, isHR } from '@/lib/auth';
import api from '@/lib/api';

function canManageAnnouncements() {
  return isAdmin() || isHR();
}

export default function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', priority: 'normal' });
  const manage = canManageAnnouncements();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/api/announcements'); setAnnouncements(data); } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.title || !form.content) { toast.error('Fill all fields'); return; }
    try {
      await api.post('/api/announcements', form);
      toast.success('Announcement posted');
      setAddOpen(false);
      setForm({ title: '', content: '', priority: 'normal' });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await api.delete(`/api/announcements/${id}`);
      toast.success('Announcement deleted');
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleTogglePin = async (id, currentlyPinned) => {
    try {
      await api.put(`/api/announcements/${id}/pin`);
      toast.success(currentlyPinned ? 'Unpinned' : 'Pinned');
      load();
    } catch (e) { toast.error('Failed to update pin'); }
  };

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Badge className="bg-[#0D2850] text-white text-[10px] mb-2 hover:bg-[#0D2850]">// ANNOUNCEMENTS</Badge>
          <h1 className="text-2xl font-semibold text-[#0B1B33] tracking-tight">Announcements</h1>
        </div>
        {manage && (
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0D2850] hover:bg-[#0B2246]" data-testid="new-announcement-btn">
                <Plus className="w-4 h-4 mr-1" />New Announcement
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Announcement</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="announcement-title-input" /></div>
                <div><Label>Content</Label><Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={4} data-testid="announcement-content-input" /></div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={v => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="announcement-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High Priority</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAdd} className="w-full bg-[#0D2850] hover:bg-[#0B2246]" data-testid="post-announcement-btn">Post Announcement</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {loading ? <div className="space-y-4"><Skeleton className="h-24" /><Skeleton className="h-24" /></div> : announcements.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-12 text-center"><Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-sm text-slate-400">No announcements yet</p></CardContent></Card>
      ) : (
        <div className="space-y-4">
          {announcements.map(a => (
            <Card key={a.id} className={`border-slate-200 border-l-4 ${a.pinned ? 'border-l-[#0D2850] bg-[#F7FAFF]' : a.auto_generated ? 'border-l-blue-400' : 'border-l-[#F5CB08]'}`} data-testid={`announcement-${a.id}`}>
              <CardContent className="p-5">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {a.pinned && <Pin className="w-3.5 h-3.5 text-[#0D2850] shrink-0" />}
                      <h3 className="text-base font-semibold text-[#0B1B33]">{a.title}</h3>
                      {a.priority === 'high' && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px]">High Priority</Badge>}
                      {a.pinned && <Badge className="bg-[#0D2850]/10 text-[#0D2850] hover:bg-[#0D2850]/10 text-[10px]">Pinned</Badge>}
                      {a.auto_generated && <Badge className="bg-blue-50 text-blue-600 hover:bg-blue-50 text-[10px] gap-1"><Bot className="w-2.5 h-2.5" />Auto</Badge>}
                    </div>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{a.content}</p>
                    <p className="text-xs text-slate-400 mt-3">
                      {a.created_at?.split('T')[0]}
                      {a.created_by ? ` \u2022 by ${a.created_by}` : ''}
                    </p>
                  </div>
                  {manage && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${a.pinned ? 'text-[#0D2850]' : 'text-slate-400 hover:text-[#0D2850]'}`} onClick={() => handleTogglePin(a.id, a.pinned)} data-testid={`pin-announcement-${a.id}`} title={a.pinned ? 'Unpin' : 'Pin to top'}>
                        {a.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => handleDelete(a.id)} data-testid={`delete-announcement-${a.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
